

























import { VueCustom, CustomVueConstructor, CustomVueType } from 'vue-custom';
import { Element, LibrarySortBy } from 'types';
import { mapGetters } from 'stores';
import debounce from 'lodash/debounce';
import { getOuterHeight, delay } from 'utils';
import { CHECK_UNDER_LIBRARY_DELAY, CONFIG_LIBRARY_SORTING_ENABLED } from 'config';
import { OUTER_LIBRARY_ID } from 'constants/misc';
import { LIBRARY_ID } from 'constants/drag';

import EventsManager from 'managers/events.manager';
import ScrollManager from 'managers/scroll.manager';

import { notEmpty } from 'helpers/types';
import { getLibraryDroppableSize, getLibraryBoxSize } from 'helpers/size';

import LibraryScroller from './scroller.vue';

const REGEX_FORBIDDEN_CHARS = /[|\\{}()[\]^$+*?.]/g;

type ComponentCustom = {
  $refs: {
    element: HTMLDivElement,
    scroller: CustomVueType,
  },
};
export default (VueCustom as CustomVueConstructor<ComponentCustom>).extend({
  name: 'library',
  components: {
    LibraryScroller,
  },
  data: () => ({
    OUTER_LIBRARY_ID,
    outerHeight: 0,
    elementOuterHeight: 0,
  }),
  computed: {
    ...mapGetters({
      libraryElementsIds: 'library',
      hasElements: 'hasLibrary',
      elementById: 'elementById',
      isSearchActive: 'isSearchActive',
      searchQuery: 'searchQuery',
      hasEmptyLibrary: 'hasEmptyLibrary',
      sorting: 'librarySortBy',
    }),
    elements(): Element[] {
      return this.libraryElementsIds
        .map((id) => this.elementById(id))
        .filter(notEmpty);
    },
    filteredByQuery(): Element[] {
      if (!this.searchQuery) {
        return [];
      }

      let query = this.searchQuery.toLowerCase().trim();
      query = query.replace(REGEX_FORBIDDEN_CHARS, '');
      const pattern = new RegExp(query);
      return this.elements.filter(({ name }) => pattern.test(name.toLowerCase()));
    },
    libraryCurrentElements(): Element[] {
      if (this.isSearchActive && this.searchQuery && this.searchQuery.length) {
        return this.filteredByQuery;
      }

      return this.elements;
    },
    elementsCount(): number {
      return this.libraryCurrentElements.length;
    },
    elementHeight(): number {
      const dim = this.$store.getters.libraryBoxDimensions;
      return dim.height + dim.margin;
    },

    isSortingEnabled(): boolean {
      return CONFIG_LIBRARY_SORTING_ENABLED;
    },
  },
  mounted() {
    this.checkUnderLibrary = debounce(this.checkUnderLibrary.bind(this), CHECK_UNDER_LIBRARY_DELAY);

    this.updateDroppableData();
    EventsManager.onResize(async () => {
      await delay(100);
      this.onResize();
    });

    this.libraryRendered().then(() => this.onRendered());
    this.onEmptyLibrary().then(() => this.onEmpty());

    this.initScrollRefresher();
  },
  watch: {
    searchQuery(query, old) {
      if (query === old || (query && !query.length)) return;

      this.refreshScroll();
    },
  },
  methods: {
    onRendered() {
      this.updateLibraryBoxDimensions();

      // order is important cause LIBRARY_RENDERED initiates iScroll
      EventsManager.emit('LIBRARY_RENDERED');
      this.$store.dispatch('libraryRendered');
    },
    onEmpty() {
      EventsManager.emit('LIBRARY_EMPTY');
      this.$store.dispatch('libraryRendered');
    },

    onLongpress({ target }: { target: HTMLDivElement }) {
      if (!target.parentNode) return;

      const vueInstance = (target.parentNode as any).__vue__;
      if (!vueInstance || !vueInstance.element) return;

      const id: string = vueInstance.element.id;
      EventsManager.emit('ENCYCLOPEDIA_SHOW_ITEM', id);
    },


    onResize() {
      this.updateDroppableData();
      this.updateLibraryBoxDimensions();
      const updated = this.updateHeights();
      if (updated) {
        this.refreshScroll();
      }

      this.checkUnderLibrary();
    },
    updateDroppableData() {
      const dimensions = getLibraryDroppableSize(this.$refs.element);
      this.$store.commit('STATIC_DROPPABLE_UPDATE', {
        id: LIBRARY_ID,
        ...dimensions,
      });
    },
    updateLibraryBoxDimensions() {
      const scrollerChildren = this.$refs.scroller.$children;
      if (!scrollerChildren.length) return;

      const element = scrollerChildren[0].$el.querySelector('img');
      if (!element) return;

      this.$store.dispatch('dimensionsLibraryBox', getLibraryBoxSize(element));
    },
    updateHeights() {
      const children = this.$refs.scroller.$children;

      if (ScrollManager.isNative || !this.$refs.element.firstChild) return false;
      if (children.length < 2) return false;

      const element = children[1].$el as HTMLElement;
      const elementHeight = element.offsetHeight;
      const margin = (getOuterHeight(element) - elementHeight) / 2;

      this.elementOuterHeight = elementHeight + margin;
      this.outerHeight = (this.$refs.element.firstChild as HTMLElement).offsetHeight;
      return true;
    },
    checkUnderLibrary() {
      this.$store.dispatch('workspaceRemoveUnderLibrary');
    },

    changeSorting(sortBy: LibrarySortBy) {
      this.$store.dispatch('libraryChangeSorting', sortBy);
    },


    async initScrollRefresher() {
      await ScrollManager.onInit();
      if (ScrollManager.isNative) return;

      this.updateHeights();
      this.$watch(() => this.libraryCurrentElements, () => this.refreshScroll());
    },
    refreshScroll() {
      const count = this.elementsCount;

      let height = this.outerHeight;
      if (height < count * this.elementHeight) {
        height = (count * this.elementHeight) + 20;
      }

      if (ScrollManager.handler && ScrollManager.handler.refresh) {
        ScrollManager.handler.refresh(height);
      }
    },


    libraryRendered() {
      return new Promise((resolve) => {
        const unwatch = this.$watch(() => this.hasElements, (value) => {
          if (!value) return;

          this.$nextTick(() => window.setTimeout(resolve, 30));
          unwatch();
        });
      });
    },
    onEmptyLibrary() {
      return new Promise((resolve) => {
        const unwatch = this.$watch(() => this.hasEmptyLibrary, (value) => {
          if (!value) return;

          resolve();
          unwatch();
        });
      });
    },
  },
});
